import React, { useState } from "react";
import { Button, ButtonVariants, FacilityGroup, FacilityGroups } from "@app/shared";
import { useFetch } from "../../../../../hooks";
import { Icons } from "../../../../shared";

interface LocationGroupsStepProps {
    organizationId: string;
    organizationName: string;
    onNext: (selectedGroupId?: string, newGroupName?: string) => void;
    onBack?: () => void;
}

export const LocationGroupsStep: React.FC<LocationGroupsStepProps> = ({ organizationName, organizationId, onNext, onBack }) => {
    const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);
    const [showCreateNew, setShowCreateNew] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [facilityGroups, , fetchHelpers] = useFetch(() => FacilityGroups.getByOrganizationId(organizationId), [organizationId]);

    const activeGroups = facilityGroups?.filter((group) => !group.isDeleted) || [];
    const hasExistingGroups = activeGroups.length > 0;

    const handleSelectGroup = (groupId: string | undefined) => {
        setSelectedGroupId(groupId);
        setShowCreateNew(false);
    };

    const handleCreateNewClick = () => {
        setShowCreateNew(true);
        setSelectedGroupId(undefined);
    };

    const handleContinueWithExisting = () => {
        if (selectedGroupId) {
            const selectedGroup = activeGroups.find((g) => g.id === selectedGroupId);
            onNext(selectedGroupId, selectedGroup?.name);
        }
    };

    const handleCreateNewGroup = async () => {
        if (!newGroupName.trim()) return;
        setIsCreating(true);
        try {
            const facilityGroup = new FacilityGroup();
            facilityGroup.name = newGroupName.trim();
            facilityGroup.organizationId = organizationId;
            const id = await FacilityGroups.save(facilityGroup);
            fetchHelpers.refreshData();
            onNext(id, newGroupName.trim());
        } catch (error) {
            console.error("Error creating group:", error);
        } finally {
            setIsCreating(false);
        }
    };

    const handleCancelCreation = () => {
        setShowCreateNew(false);
        setNewGroupName("");
    };

    if (fetchHelpers.isLoading) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                    <p className="text-neutral-600">Loading location groups...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icons.LocationOutline className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">Choose Location Group</h2>
                <p className="text-neutral-600 mb-4">{hasExistingGroups ? "Select an existing location group or create a new one for your device" : "You don't have any location groups yet. Let's create your first one!"}</p>
                <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
                    <span className="text-neutral-700 font-medium">Organization: {organizationName}</span>
                </div>
            </div>

            {hasExistingGroups && (
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">Existing Location Groups</h3>
                    <div className="grid gap-3">
                        {activeGroups.map((group) => (
                            <div key={group.id} onClick={() => handleSelectGroup(group.id)} className={`group p-5 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${selectedGroupId === group.id ? "border-brand-primary bg-terracotta-50 shadow-md ring-1 ring-brand-primary ring-opacity-30 shadow-brand-glow" : "border-warmGray-200 hover:border-warmGray-300 hover:bg-warmGray-50"}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 transition-colors ${selectedGroupId === group.id ? "bg-brand-primary" : "bg-warmGray-100 group-hover:bg-warmGray-200"}`}>
                                            <Icons.LocationOutline className={`w-6 h-6 ${selectedGroupId === group.id ? "text-white" : "text-warmGray-600"}`} />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-neutral-900 mb-1">{group.name}</h4>
                                            <p className="text-sm text-neutral-500">
                                                {group.facilityCount} {group.facilityCount === 1 ? "location" : "locations"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        {selectedGroupId === group.id && (
                                            <div className="w-5 h-5 bg-brand-primary rounded-full flex items-center justify-center mr-3">
                                                <div className="w-2 h-2 bg-white rounded-full"></div>
                                            </div>
                                        )}
                                        <Icons.ChevronRight className="w-5 h-5 text-warmGray-400 group-hover:text-warmGray-600 transition-colors" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {selectedGroupId && (
                        <div className="mt-4">
                            <Button onClick={handleContinueWithExisting} className="w-full bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl">
                                Continue with Selected Group
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <div className="mb-8">
                {hasExistingGroups && (
                    <div className="flex items-center mb-6">
                        <div className="flex-1 border-t border-warmGray-200"></div>
                        <span className="px-4 text-warmGray-500 text-sm font-medium">OR</span>
                        <div className="flex-1 border-t border-warmGray-200"></div>
                    </div>
                )}

                <button onClick={handleCreateNewClick} className={`w-full h-20 p-5 border-2 border-dashed rounded-xl transition-all group ${showCreateNew ? "border-brand-primary bg-terracotta-50" : "border-warmGray-300 hover:border-brand-primary hover:bg-terracotta-50"}`}>
                    <div className="flex items-center justify-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${showCreateNew ? "bg-brand-primary" : "bg-warmGray-100 group-hover:bg-brand-primary"}`}>
                            <Icons.AddOutline className={`w-5 h-5 transition-colors ${showCreateNew ? "text-white" : "text-warmGray-600 group-hover:text-white"}`} />
                        </div>
                        <span className="text-neutral-700 font-semibold">Create New Location Group</span>
                    </div>
                </button>

                {showCreateNew && (
                    <div className="mt-4 p-6 bg-gradient-to-r from-terracotta-50 to-warmGray-50 border border-brand-primary border-opacity-30 rounded-xl">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-neutral-700 mb-3">Location Group Name</label>
                                <input
                                    type="text"
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    placeholder="e.g., Main Building, Warehouse A, North Campus..."
                                    className="w-full px-4 py-3 border border-warmGray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && newGroupName.trim()) {
                                            handleCreateNewGroup();
                                        }
                                    }}
                                />
                            </div>

                            <div className="flex gap-3">
                                <Button onClick={handleCreateNewGroup} disabled={!newGroupName.trim() || isCreating} className="flex-1 bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed">
                                    {isCreating ? "Creating..." : "Create Group"}
                                </Button>
                                <Button variant={ButtonVariants.Secondary} onClick={handleCancelCreation} className="flex-1 bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50">
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-gradient-to-r from-warmGray-50 to-white border border-warmGray-100 rounded-xl p-6">
                <div className="flex items-start">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <Icons.LocationOutline className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-neutral-900 mb-2">What is a Location Group?</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">A location group helps you organize multiple locations or areas within your organization. For example, you might have "Main Parking" or "Main Campus" as location groups, each containing multiple locations.</p>
                    </div>
                </div>
            </div>

            {onBack && (
                <div className="mt-6 text-center">
                    <button onClick={onBack} className="inline-flex items-center text-warmGray-500 hover:text-brand-primary text-sm font-medium transition-colors">
                        <Icons.ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                        Back
                    </button>
                </div>
            )}
        </div>
    );
};
